import { Download, ImageOff, Trash2 } from "lucide-react";
import { Link } from "react-router";

import {
  Button,
  Card,
  Tooltip,
  cn,
  insetFocusRingClassName,
  interactiveMotionClassName,
} from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { ROUTES } from "@/navigation/constants/routes";
import { ProjectMetaBadges } from "@/music-catalog/components/music-catalog-page/project-meta-badges";
import { ProjectCoverTransition } from "@/music-catalog/components/project-cover-transition";
import {
  resolveProjectDownloadName,
  resolveProjectDownloadUrl,
} from "@/music-catalog/utils/catalog-view";
import { resolveProjectArtworkPanels } from "@/music-catalog/utils/project-artwork";
import { resolveProjectDiscWarmUpHandlers } from "@/music-catalog/utils/project-disc-warm-up";

import type { LibraryProject } from "@/cloud/types/types";

export const ProjectCard = ({
  duplicateCount,
  isRemoving,
  onRemove,
  project,
}: {
  duplicateCount: number;
  isRemoving: boolean;
  onRemove: (project: LibraryProject) => void;
  project: LibraryProject;
}) => {
  const { t } = useTranslation();
  // The catalog identifies an album by its Front. A legacy project without one
  // gets the same quiet placeholder as Desktop rather than changing the meaning
  // of this grid from card to card by substituting its Disc or Back.
  const artwork = resolveProjectArtworkPanels(project).find((panel) => panel.id === "front");

  return (
    <Card
      padding="none"
      className={cn(
        "group flex h-full flex-col overflow-hidden",
        "transition-[box-shadow,border-color,transform] duration-200 ease-out",
        "hover:scale-[1.01] hover:border-primary/40 hover:shadow-floating",
        "motion-reduce:transition-none motion-reduce:hover:scale-100",
      )}
    >
      {/*
        The artwork and the title are one link to the project, so everything
        that identifies it is the target. The actions below are siblings rather
        than children of it: a link may not contain a button.
      */}
      <Link
        to={`${ROUTES.catalog}/${encodeURIComponent(project.desktopProjectId)}`}
        className={cn("block rounded-t-xl", interactiveMotionClassName, insetFocusRingClassName)}
        {...resolveProjectDiscWarmUpHandlers(project)}
      >
        {/*
          The Front is the project's own artwork, so it carries an empty alt
          and the title below is the name: describing it would have a screen
          reader read the title twice.
        */}
        {artwork === undefined ? (
          <div className="flex aspect-square w-full items-center justify-center bg-muted">
            <ImageOff aria-hidden className="size-12 text-muted-foreground" />
          </div>
        ) : (
          <ProjectCoverTransition desktopProjectId={project.desktopProjectId}>
            <img
              src={artwork.url}
              alt=""
              loading="lazy"
              className="aspect-square w-full object-contain"
            />
          </ProjectCoverTransition>
        )}
        <span className="block border-t border-border px-3 pt-3">
          <span className="block truncate font-medium">{project.title}</span>
        </span>
      </Link>

      <div className="flex flex-1 flex-col gap-2 px-3 pt-2 pb-3">
        <ProjectMetaBadges duplicateCount={duplicateCount} project={project} />

        <div className="mt-auto flex items-center justify-end gap-1">
          <Tooltip content={t("catalog.download")} side="top">
            <Button
              asChild
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t("catalog.downloadProject", { title: project.title })}
            >
              <a
                href={resolveProjectDownloadUrl(project)}
                download={resolveProjectDownloadName(project)}
              >
                <Download />
              </a>
            </Button>
          </Tooltip>
          {/*
          The same control the Devices page draws, because removing a thing from
          a list is one action however many lists there are: two different
          delete buttons read as two applications.
        */}
          <Tooltip content={t("catalog.remove")} side="top">
            <Button
              type="button"
              variant="destructiveGhost"
              size="icon"
              className="h-8 w-8"
              aria-label={t("catalog.removeProject", { title: project.title })}
              isLoading={isRemoving}
              onClick={() => onRemove(project)}
            >
              {isRemoving ? undefined : <Trash2 />}
            </Button>
          </Tooltip>
        </div>
      </div>
    </Card>
  );
};
